import React, { useCallback, useEffect, useState } from "react";
import type { Dispatch } from "redux";
import { useDispatch, useSelector } from "react-redux";
import { selectOrderByUuid } from "./reducers/selectors.tsx";

import EditableTextArea from "../../react/EditableTextArea";
import { translate } from "../../IntlProviderWrapper";
import * as Auth from "../../../lib/Auth";
import { useUpdateOrderComment } from "../../../lib/custom-hooks/queryComment";
import { onSaveComment } from "./PlanningTableEditModal";

import type { ReduxState, ActionTypes } from "./reducers/reducers.tsx";

type Props = {
    order_uuid: string;
    comment: string;
};

const OrderCommentTextArea = (props: Props) => {
    const { order_uuid, comment } = props;

    const [inputComment, setInputComment] = useState(comment);

    const order = useSelector((state: ReduxState) => selectOrderByUuid(state, order_uuid));
    const reduxDispatch = useDispatch<Dispatch<ActionTypes>>();

    const updateOrderComment = useUpdateOrderComment();

    useEffect(() => {
        setInputComment(comment);
    }, [comment, order]);

    const handleTextareaChange = useCallback((value: string) => {
        setInputComment(value);
    }, []);

    const handleSave = useCallback(
        async (event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();

            if (!order) {
                return;
            }

            try {
                await updateOrderComment.mutateAsync({ order, comment: inputComment });
                onSaveComment(order_uuid, inputComment, reduxDispatch);
            } catch (e) {
                // Error is handled by the mutation hook rollback.
                console.log((e as Error).message);
            }
        },
        [order, inputComment, order_uuid, reduxDispatch, updateOrderComment]
    );

    const isDisabled = !Auth.hasPermission(Auth.PERMISSION_NAMES.OrderEdit);
    const showSave = inputComment !== comment;

    return (
        <div className="d-flex flex-column gap-1">
            <div className="font-weight-bold">{translate("common.comment", "Comment")}:</div>
            <div className="d-flex gap-2">
                <EditableTextArea
                    value={inputComment}
                    onChange={handleTextareaChange}
                    isDisabled={isDisabled}
                    title={translate("common.comment", "Comment")}
                />
                {showSave && (
                    <button id="comment-edit-button" className="btn btn-outline-primary" onClick={handleSave}>
                        {translate("common.save", "Save")}
                    </button>
                )}
            </div>
        </div>
    );
};

export default OrderCommentTextArea;
